import { useEffect, useMemo, useRef, useState } from 'react';
import { useLoaderData, useNavigate, useNavigation } from '@remix-run/react';
import 'videojs-contrib-ads/dist/videojs.ads.css?url';
import videojs from 'video.js';
import 'videojs-contrib-ads';
import 'videojs-ima';
import 'video.js/dist/video-js.css?url';
import 'videojs-ima/dist/videojs.ima.css?url';
import 'videojs-mux';
// @ts-ignore
import videojsContribEme from 'videojs-contrib-eme';
// @ts-ignore
import youbora from 'youboralib';
import 'youbora-adapter-videojs';
import type Player from 'video.js/dist/types/player';

import continueWatchingPlugin from './plugins/continue-watching.ts';
import playNextPlugin from './plugins/play-next.ts';
import analyticsPlugin from './plugins/analytics.ts';
import topBarPlugin from './plugins/top-bar.ts';
import jwAnalyticsPlugin from './plugins/jw-analytics';
import chapterMarkersPlugin from './plugins/chapter-markers.ts';
import muxAnalyticsPlugin from './plugins/mux-analytics.ts';
import { PlayerPlugin } from './types.ts';
import { trackPlayerError, trackPlayerSessionEnd } from './tracking.ts';
import {
  isLive,
  getBoolValue,
  getMuxVideoAnalyticsParams,
} from './utils/index.ts';
import { drmSetup } from './drm/drm-setup.ts';
import type { VideojsOptions } from './types.ts';
import type { Feed } from '~/services/layout/index.ts';
import type { PlayerTrackData } from './tracking.ts';
import type { NavigateFunction } from '@remix-run/react';
import type {
  AnalyticsSharedVars,
  MuxAnalyticsProps,
} from '~/services/analytics/events-names.ts';
import { PlayNext } from './play-next.tsx';
import {
  getMidrollOffsetFromXml,
  isPostrollBreakIdInXml,
  videojsAdvertisement,
} from './advertisement/index.ts';
import { AdsBackButton } from './ads-back-button.tsx';
import { useIsGoogleIma } from './advertisement/use-is-google-ima.ts';

const playerID = 'vjs_video_3';

export type LoaderData = {
  isMock: boolean;
  analyticsSharedVars: AnalyticsSharedVars;
  isContinueWatchingSyncedFeed: boolean;
  screenId: string;
  accountId: string;
  policyKey: string;
  viewerId: string;
  styles: any;
  muxAnalytics: MuxAnalyticsProps;
};

export default function VideojsPlayer({
  playableItem,
  pluginsData,
  appData,
  isNavigatingBack,
  playNextUrl,
  vmapXml,
}: {
  playableItem: any;
  videoId?: string;
  pluginsData: any;
  appData: any;
  isNavigatingBack: boolean;
  playNextUrl: string | null;
  vmapXml: string;
}) {
  const {
    analyticsSharedVars,
    isMock,
    isContinueWatchingSyncedFeed,
    styles,
    muxAnalytics,
  } = useLoaderData<LoaderData>();

  const [isRunningPlayNext, setIsRunningPlayNext] = useState<boolean>(false);
  const [duration, setDuration] = useState<number>(0);
  const [currentPosition, setCurrentPosition] = useState<number>(0);
  const [playNextOverlayDuration, setPlayNextOverlayDuration] =
    useState<number>(0);
  const [playNextFeed, setPlayNextFeed] = useState<Feed | null>(null);
  const [showPlayNextOnPostroll, setShowPlayNextOnPostroll] =
    useState<boolean>(false);
  const [showAdsBackButton, setShowAdsBackButton] = useState<boolean>(false);

  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const youboraRef = useRef<any | null>(null);

  const playNextFeedRef = useRef<Feed | null>(null);
  const isRunningPlayNextRef = useRef(false);
  const closePlayNextRef = useRef<boolean>(false);

  playNextFeedRef.current = playNextFeed;
  isRunningPlayNextRef.current = isRunningPlayNext;

  const navigate: NavigateFunction = useNavigate();
  const navigation = useNavigation();
  const streamType = useMemo(
    () => (isLive(playableItem) ? 'live' : 'vod'),
    [playableItem]
  );
  const isFreeItem =
    playableItem.extensions?.free || playableItem.extensions?.isFree;
  const analyticsCustomProperties = useMemo(
    () =>
      playableItem.extensions?.analyticsCustomProperties &&
      JSON.stringify(playableItem.extensions?.analyticsCustomProperties),
    [playableItem.extensions?.analyticsCustomProperties]
  );

  const trackData: PlayerTrackData = useMemo(
    () => ({
      ...analyticsSharedVars,
      name: playableItem?.title,
      media_id: playableItem?.id,
      media_type: playableItem.type?.value,
      duration: playableItem.extensions?.duration,
      stream_format: playableItem?.content?.type,
      stream_type: streamType,
      free_or_paid: getBoolValue(isFreeItem) ? 'free' : 'paid',
      analyticsCustomProperties,
    }),
    [
      analyticsSharedVars,
      playableItem?.title,
      playableItem?.id,
      playableItem?.content?.type,
      playableItem.extensions?.duration,
      playableItem.type?.value,
      streamType,
      isFreeItem,
      analyticsCustomProperties,
    ]
  );

  const isPostroll: boolean = useMemo(() => {
    return isPostrollBreakIdInXml(vmapXml);
  }, []);

  const midrollTimeOffset: number = useMemo(() => {
    return getMidrollOffsetFromXml(vmapXml);
  }, []);

  const playNext = (event: any) => {
    if (!playNextUrl || closePlayNextRef.current) return;

    const isClickEvent: boolean = event?.type === 'click';

    if (isPostroll && !isClickEvent) return;

    playerRef.current?.reset();

    navigate(`/player?self-link=${encodeURIComponent(playNextUrl)}`, {
      replace: true,
    });
  };

  const options: VideojsOptions = useMemo(() => {
    const playerOptions = {
      id: playerID,
      controls: true,
      controlBar: {
        volumePanel: {
          inline: false,
        },
      },
      fill: true,
      autoplay: true,
      bigPlayButton: false,
      experimentalSvgIcons: true,
      liveui: true,
      plugins: {
        continueWatchingPlugin: {
          entryId: playableItem.id,
          resumeTime: playableItem.extensions?.resumeTime,
          duration: playableItem.extensions?.duration,
          entry: playableItem,
          isContinueWatchingSyncedFeed,
          continueWatchingPluginConfiguration:
            pluginsData?.continueWatching?.customConfiguration,
        },
        playNextPlugin: {
          setIsRunningPlayNext,
          setPlayNextFeed,
          playNextFeedUrl: playNextUrl,
          setDuration,
          setCurrentPosition,
          playNext,
          isPostroll,
          playNextOverlayDuration,
        },
        analyticsPlugin: { analyticsSharedVars, trackData },
        topBarPlugin: {
          playerID,
          navigate,
          title: playableItem?.title,
          playableItem,
          analyticsTrackData: trackData,
        },
        chapterMarkersPlugin: {
          playerID,
          playableItem,
          configuration: styles,
          setPlayNextOverlayDuration,
        },
      },
    };

    if (pluginsData?.jw_token) {
      // @ts-ignore
      playerOptions.plugins.jwAnalyticsPlugin = {
        token: pluginsData?.jw_token,
        userId: pluginsData?.jwUserId,
        trackData,
        playableItem,
      };
    }

    if (muxAnalytics?.enabled && muxAnalytics?.env_key) {
      const data = {
        env_key: muxAnalytics?.env_key,
        // Site Metadata
        viewer_user_id: pluginsData?.muxUserId,
        // Player Metadata
        player_name: playerID,
        // Video Metadata
        ...getMuxVideoAnalyticsParams(muxAnalytics, playableItem),
      };

      // @ts-ignore
      playerOptions.plugins.mux = {
        debug: false,
        data,
      };
    }

    return playerOptions;
  }, [playableItem, playNextUrl, analyticsSharedVars, trackData, navigate]);

  useEffect(() => {
    return () => {
      trackPlayerSessionEnd(trackData, 0);
    };
  }, []);

  useEffect(() => {
    if (navigation.state === 'loading') return;
    if (isNavigatingBack) return;

    const src = playableItem?.content?.src;
    const type = playableItem?.content?.type;

    const { drm } = playableItem.extensions || {};

    const videoElement = document.createElement('video-js');

    if (!videoElement) return;

    videoElement.classList.add('vjs-big-play-centered');

    const videoContainer = videoRef.current;

    if (!videoContainer) return;
    videoContainer.appendChild(videoElement);

    if (!videojs.getPlugin(PlayerPlugin.continueWatchingPlugin)) {
      videojs.registerPlugin(
        PlayerPlugin.continueWatchingPlugin,
        continueWatchingPlugin
      );
    }

    if (!videojs.getPlugin(PlayerPlugin.playNextPlugin)) {
      videojs.registerPlugin(PlayerPlugin.playNextPlugin, playNextPlugin);
    }

    if (!videojs.getPlugin(PlayerPlugin.analyticsPlugin)) {
      videojs.registerPlugin(PlayerPlugin.analyticsPlugin, analyticsPlugin);
    }

    if (!videojs.getPlugin(PlayerPlugin.topBarPlugin)) {
      videojs.registerPlugin(PlayerPlugin.topBarPlugin, topBarPlugin);
    }

    if (
      !videojs.getPlugin(PlayerPlugin.jwAnalyticsPlugin) &&
      pluginsData?.jw_token
    ) {
      videojs.registerPlugin(PlayerPlugin.jwAnalyticsPlugin, jwAnalyticsPlugin);
    }

    if (!videojs.getPlugin(PlayerPlugin.chapterMarkersPlugin)) {
      videojs.registerPlugin(
        PlayerPlugin.chapterMarkersPlugin,
        chapterMarkersPlugin
      );
    }

    if (
      !videojs.getPlugin(PlayerPlugin.mux) &&
      muxAnalytics?.enabled &&
      muxAnalytics?.env_key
    ) {
      videojs.registerPlugin(PlayerPlugin.mux, muxAnalyticsPlugin);
    }

    playerRef.current = videojs(
      videoElement,
      { ...options, id: playerID },
      () => {
        videojs.log('player is ready', { playableItem });
      }
    );

    const player = playerRef.current;

    if (typeof youbora !== 'undefined' && pluginsData?.youboraAccountCode) {
      youboraRef.current = new youbora.Plugin({
        accountCode: pluginsData?.youboraAccountCode,
      });
      const youboraAdapter = youboraRef.current;

      const cdnKey = pluginsData?.analyticsMapper?.cdn;

      youboraAdapter.setOptions({
        'app.name': appData?.appName,
        'app.releaseVersion': appData?.appVersion,
        'content.isLive': playableItem?.extensions?.isLive,
        'content.title': playableItem?.extensions?.guid || playableItem?.title,
        'content.cdn': cdnKey
          ? playableItem?.[cdnKey]
          : playableItem?.extensions?.cdn,
        'user.name': pluginsData?.userId,
      }); // metadata options, https://documentation.npaw.com/integration-docs/docs/setting-options-and-metadata.

      youboraAdapter.setAdapter(new youbora.adapters.Videojs(player)); // Attach adapter
    }

    player.on('loadedmetadata', () => {
      // FIXME: textTracks doesn't exist
      // @ts-ignore
      const tracks: any = player.textTracks();
      for (let i = 0; i < tracks.length; i++) {
        if (tracks[i].kind === 'captions' || tracks[i].kind === 'subtitles') {
          tracks[i].mode = 'disabled';
        }
      }

      // @ts-ignore
      const liveTracker = player.liveTracker;

      if (liveTracker && liveTracker.isLive()) {
        // Seek to the live point
        player.currentTime(liveTracker.seekableEnd());
      }
    });

    // Hide context menu for player
    player?.on('contextmenu', function (e: any) {
      e?.preventDefault();
    });

    try {
      if (isMock) {
        player.src({
          src: require('../../mocks/data/movie.mp4'),
          type: 'video/mp4',
        });

        return;
      }
      if (drm) {
        videojs.registerPlugin('eme', videojsContribEme);

        const sourceExtras = drmSetup(player, drm, type);

        player.src([{ src, ...sourceExtras }]);
      } else {
        player.src([{ src, type: 'application/x-mpegURL' }]);
      }
    } catch (error) {
      videojs.log('player failed to set video source', { playableItem });

      trackPlayerError(trackData, currentPosition || 0);
    }

    return () => {
      setIsRunningPlayNext(false);
      playNextFeedRef.current = null;
      isRunningPlayNextRef.current = false;

      if (!player) return;

      if (!document.pictureInPictureElement) player.dispose();
    };
  }, [options, playableItem]);

  const isGoogleImaSdkReady: boolean = useIsGoogleIma();

  useEffect(() => {
    if (!isGoogleImaSdkReady) return;

    videojsAdvertisement({
      playerID,
      player: playerRef.current as Player,
      vmapXml,
      midrollTimeOffset,
      isPostroll,
      setShowAdsBackButton,
      setShowPlayNextOnPostroll,
    });
  }, [options, playableItem, isGoogleImaSdkReady]);

  return (
    <div data-vjs-player className="player">
      {showAdsBackButton && <AdsBackButton playableItem={playableItem} />}
      <div
        ref={videoRef}
        className="h-screen overflow-y-hidden overscroll-none scrollbar-hide"
      />
      {isRunningPlayNext && playNextFeed && !closePlayNextRef.current && (
        <PlayNext
          playNext={playNext}
          feed={playNextFeed}
          totalDuration={duration}
          position={currentPosition}
          trackData={trackData}
          isPostroll={isPostroll}
          showPlayNextOnPostroll={showPlayNextOnPostroll}
          closePlayNextRef={closePlayNextRef}
        />
      )}
    </div>
  );
}
